import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Dashboard from './dashboard/Dashboard';
import Layout from './Layout';
import Login from './dashboard/Login';
import Register from './dashboard/Signup';
import Settings from './account/Settings';
import Campaigns from './dashboard/Campaigns';
import Campaign from './dashboard/Campaign';
import Examples from './dashboard/Examples';
import Play from './games/Play';
import Editor from './games/Editor';
import { UserProvider, useUser } from './userContext';
import { ClientProvider } from './clientContext';
import PrivateRoute from './PrivateRoute';
import Support from './dashboard/Support';
import SharedByUsers from './dashboard/SharedByUsers';
import ScrollToTop from './dashboard/ScrollToTop';
import PlanModal from './dashboard/PlanModal';

import Error, { Studio404 } from './account/ErrorPage';
import NewCampaign from './campaign/CreateCampaign';
import ClientHome from './clients/clientHome';
import EditClient from './clients/EditClient';
import DefaultLoading from './common/DefaultLoading';
import PublicGameCreator from './games/PublicGameCreator';
import ArcadeLanding from './pages/ArcadeLanding';
import AdminDashboard from './pages/AdminDashboard';
import AdminRoute from './components/AdminRoute';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import SupportPage from './pages/Support';
import { initializeDynamicSEO } from './utils/seo';
import { initGA, trackPageView } from './utils/analytics';

// Add the hook to load reCAPTCHA script dynamically
function useRecaptchaScript() {
  React.useEffect(() => {
    if (document.querySelector('script[src^="https://www.google.com/recaptcha/api.js"]')) {
      return;
    }
    
    const script = document.createElement('script');
    const scriptUrl = `https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`;
    script.src = scriptUrl;
    script.async = true;
    
    script.onload = () => {
      // reCAPTCHA script loaded successfully
    };
    
    script.onerror = (error) => {
      // Failed to load reCAPTCHA script
    };
    
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
}

const AppContent = () => {
  useRecaptchaScript();
  const { user, loading } = useUser();
  const location = useLocation();

  // Initialize dynamic SEO (favicon, title) and analytics on app load
  React.useEffect(() => {
    initializeDynamicSEO();
    initGA();
  }, []);

  // Track page views on route changes
  React.useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  // Skip app-level loading for public play routes
  const isPlayRoute = location.pathname.startsWith('/play/');
  
  // If loading and not a play route, show loading indicator
  if (loading && !isPlayRoute) {
    return <DefaultLoading />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Group Chat Heroes routes */}
        <Route path="/" element={<ArcadeLanding />} />
        <Route path="arcade" element={<ArcadeLanding />} />
        <Route path="arcade/create" element={<PublicGameCreator />} />
        <Route path="create" element={<PublicGameCreator />} />
        <Route path="play/:id" element={<Play />} />

        
        {/* Legal pages */}
        <Route path="privacy" element={<PrivacyPolicy />} />
        <Route path="terms" element={<TermsOfService />} />
        <Route path="support" element={<SupportPage />} />
        
        {/* Studio routes */}
        <Route path="studio">
          {/* Redirect studio landing to register page */}
          <Route index element={<Navigate to="/studio/register" replace />} />
          
          {/* Studio auth routes */}
          <Route path="login" element={user ? <Navigate to="/studio/dashboard" replace /> : <Login />} />
          <Route path="register" element={user ? <Navigate to="/studio/dashboard" replace /> : <Register />} />
          
          {/* Protected studio business routes - Layout as wrapper */}
          <Route path="*" element={<Layout />}>
            <Route path="dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="plan" element={<PrivateRoute><PlanModal /></PrivateRoute>} />
            <Route path="support" element={<PrivateRoute><Support /></PrivateRoute>} />
            <Route path="settings/*" element={<PrivateRoute><Settings /></PrivateRoute>} />
            <Route path="success" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="campaigns" element={<PrivateRoute><Campaigns /></PrivateRoute>} />
            <Route path="campaign/:id" element={<PrivateRoute><Campaign /></PrivateRoute>} />
            <Route path="games" element={<PrivateRoute><Examples /></PrivateRoute>} />
            <Route path="shared" element={<PrivateRoute><SharedByUsers /></PrivateRoute>} />
            <Route path="new" element={<PrivateRoute><NewCampaign /></PrivateRoute>} />
            <Route path="clients" element={<PrivateRoute><ClientHome /></PrivateRoute>} />
            <Route path="edit-client/:id" element={<PrivateRoute><EditClient /></PrivateRoute>} />
            <Route path="admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            
            {/* Studio catch-all route for invalid studio paths */}
            <Route path="*" element={<PrivateRoute><Studio404 /></PrivateRoute>} />
          </Route>
        </Route>

        {/* Editor route - outside of Layout */}
        <Route
          path="editor/:id"
          element={
            <PrivateRoute>
              <Editor />
            </PrivateRoute>
          }
        />

        {/* Other routes */}
        <Route path="error" element={<Error />} />

        {/* Catch all route - redirect to root */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <UserProvider>
        <ClientProvider>
          <AppContent />
        </ClientProvider>
      </UserProvider>
    </BrowserRouter>
  );
};

export default App;